import React, { memo, useEffect, useRef, useState } from "react";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import { MoveHandler } from "./MoveHandler";
import Point from "../models/Point";
import Page from "../models/Page";

import { PagePrintInfo } from "../models/PagePrintInfo";
import Logger from "../Logger";
import Browser from "../browser";
import { useRedraw } from "../models/useRerender";
import { bringElementToTop, measureText } from "./GraficElementUtils";
import type Memo from "../models/Memo";
import { RDiv } from "../components/renderPrimitives/RDiv";
import { RenderTarget, useRenderTarget } from "../components/renderPrimitives/renderTarget";
import { RText } from "../components/renderPrimitives/RText";
import MemoViewSvg from "./MemoViewSvg";
import { classicMemoLayout } from "./classicPlanSvgLayout";
import { noItx } from "../translationItx/itx";

const logger = new Logger();

/**
 * MemoView renders a single Memo object on the drawing canvas (floor plan or room plan).
 *
 * A Memo can be:
 *  - A regular free-text annotation (isRoomTag === false)
 *  - A room-label placeholder (isRoomTag === true), which displays the room's tag and
 *    name and is only visible on the floor plan. Clicking it selects the room.
 *
 * Interaction modes:
 *  - Normal view:  text is displayed; the element is draggable via MoveHandler.
 *  - Selected (non-isRoomTag): the element shows an inline editor (font-size input + textarea).
 *  - Double-click on isRoomTag: opens the room's page in a new working pane.
 *
 * Props:
 *  - memo      The Memo domain object to render.
 *  - mainPage  The Page that is currently the active drawing area (floor or room plan).
 *  - page      The Page that owns the memo (may differ from mainPage for floor-plan display).
 *  - print     If set, rendering is adjusted for PDF/print output.
 */
export default function MemoView(props: { memo: Memo; mainPage: Page; page: Page; print?: PagePrintInfo }) {
  const { memo, page, mainPage, print } = props;
  const diagnosticMode = state.settings.diagnosticMode;

  const thisElement = useRef<HTMLDivElement>(null); // ref to the root DOM element
  const fontSizeTimeoutRef = useRef<any>(null); // debounce timer for font-size changes

  const isMoving = useRef<boolean>(false); // true while the user is dragging
  const ignoreClick = useRef<boolean>(false); // suppresses the click after a drag or cancel
  const inititalSelectionState = useRef(false); // selection state at pointer-down (unused currently)
  const redraw = useRedraw();
  const target = useRenderTarget(); // web or PDF render context

  const initialPoint = useRef<Point | undefined>(undefined); // memo position at drag start (for cancel)

  const consumerRef = useRef(memo); // stable ref to memo for use inside callbacks
  const redrawTimer = useRef<any>(null); // debounce timer for zoom-triggered redraws

  // memoSelected drives the MoveHandler lifecycle (see useEffect below).
  // In diagnostic mode the selection UI is suppressed, so the handler stays active.
  const memoSelected = memo.selected && !diagnosticMode;

  // Re-render whenever global display settings change (e.g. showIcons, diagnosticMode).
  useOnChangeWatcher(state.settings);

  // Re-render on any memo change; when the memo becomes selected, raise it to the
  // top of the DOM stacking order so it appears above other elements.
  useOnChangeWatcher(memo, (changedProperty, changedObject) => {
    if (changedProperty == "selected") {
      if (memo.selected) {
        bringElementToTop(thisElement.current);
      }
    }
    return true; // always trigger a re-render
  });

  // When the user zooms, defer the redraw by 200 ms to avoid excessive re-renders
  // during continuous zoom gestures. Returns false so the watcher itself does not
  // trigger an immediate re-render.
  useOnChangeWatcher(mainPage.currentZoom, (changedProperty, changedObject) => {
    if (redrawTimer.current) {
      clearTimeout(redrawTimer.current);
    }
    redrawTimer.current = setTimeout(() => {
      redraw();
      redrawTimer.current = 0;
    }, 200);
    return false;
  });

  console.log("render memo");

  function clearFocus() {
    if (document?.activeElement) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }

  // The MoveHandler wires up native pointer events for drag-and-drop positioning.
  // It is re-created whenever the selection state changes (memoSelected) because
  // the handler must be absent while the inline editor is active (selected state),
  // allowing normal text interaction inside the textarea.
  useEffect(() => {
    // While the memo is selected the inline editor is shown; no drag handler needed.
    if (memoSelected) {
      return;
    }

    // On the floor plan the memo moves within its parent room's coordinate space.
    const room = (memo.parent as Page)?.parentRoom;

    const xmoveHandler = new MoveHandler(thisElement, null, mainPage.isFloorPlan ? room : undefined, mainPage);

    // Record the position at pointer-down so it can be restored on cancel.
    xmoveHandler.onMouseDown = () => {
      inititalSelectionState.current = memo.selected;
      ignoreClick.current = false;
      initialPoint.current = consumerRef.current.position.clone();

      logger.log(`set initial point ${initialPoint.current.x} ${initialPoint.current.y}`);
    };

    // Drag was cancelled (e.g. pointer left the element): restore the original position.
    xmoveHandler.onCancel = () => {
      ignoreClick.current = true;
      if (initialPoint.current) {
        logger.log(`restore initial point ${initialPoint.current.x} ${initialPoint.current.y}`);

        consumerRef.current.position = initialPoint.current.clone();

        logger.log(`restore initial point ${consumerRef.current.position.x} ${consumerRef.current.position.y}`);
        setTimeout(() => {
          logger.log(`restore initial point A ${consumerRef.current.position.x} ${consumerRef.current.position.y}`);
        }, 1000);
      }
      redraw();

      clearFocus();
    };

    // Pointer released without a drag: select this memo (unless the click was suppressed).
    // Note: for isRoomTag memos the React onClick handler on the outer RDiv handles
    // selection of the room instead; this path handles regular memos.
    xmoveHandler.onMouseUp = () => {
      if (!memo.selected && !ignoreClick.current) {
        state.selectionManager.selectedObject = memo;
        memo.raiseOnChange("selected");
      }
      ignoreClick.current = false;
    };

    // Double-click on any memo: open the parent room in a new working pane.
    xmoveHandler.onDblClick = (ev: MouseEvent) => {
      const room2 = (memo.parent as Page)?.parentRoom;
      if (room2) {
        room2.treeViewModel.handleOpen();
      }
    };

    // Drag has started (threshold exceeded): register an undo action so the move
    // can be rolled back, and suppress the subsequent click.
    xmoveHandler.onMoveStarted = () => {
      isMoving.current = true;
      ignoreClick.current = true;
    };

    // Drag finished: commit the new position to the domain model and mark dirty.
    xmoveHandler.onMoveFinished = (p: Point) => {
      consumerRef.current.position = new Point(
        // p.x + symbolWidth2,
        // p.y + symbolWidth2,
        p.x,
        p.y,
      );
      page.markAsDirty();

      isMoving.current = false;
    };

    // Cleanup: detach all native event listeners when the effect re-runs or the
    // component unmounts.
    return () => {
      xmoveHandler.release();
    };
  }, [memoSelected, memo, mainPage, page, redraw]);

  logger.log(`render consumer ${memo.id}`);

  const position = memo.position;

  let selectFrameClassName = "selectFrame"; // no itx
  if (print) {
    selectFrameClassName += "Print"; // no itx
  }

  if (memo.selected) {
    selectFrameClassName += " selected"; // no itx
  } else if (state.settings.showIcons) {
    //selectFrameClassName = "zoomOnHover " + selectFrameClassName;
  }
  if (memo.getConnectedChannel() == undefined) {
    selectFrameClassName += " noChannelConnection"; // no itx
  }

  let fontSize = memo.fontSize;

  const touchSelected = memo.selected && Browser.isTouch() ? "touchSelected" : ""; // no itx

  // Derive the parent room from the memo's containing page.
  const room = (memo.parent as Page)?.parentRoom;

  // Display text: regular memos show memo.name; room-tag memos show the room's
  // tag and name on one line.
  let s = memo.name;

  if (memo.isRoomTag && room) {
    s = [room.tag, room.name].filter(Boolean).join(" ");
  }
  // if (room && memo.isRoomTag && mainPage?.editRooms) {
  //   return null;
  // }

  //
  // innerhalb von Raumplänen unterdrücken wir die Raumbeschriftung,
  // da sie ohnehin im Schriftkopf steht
  //

  let sizeFromNestedRoomPlan = 1;
  // Room-tag memos are suppressed on room plans because the room header already
  // shows the room name. They are only relevant on the floor plan overview.
  if (memo.isRoomTag && !mainPage.isFloorPlan) {
    return null;
  }

  // if (!mainPage.isFloorPlan) {
  //   sizeFromNestedRoomPlan = sizeFromNestedRoomPlan / (room?.roomtoFloorDrawingTransform?.scale || 1);
  // }
  let forPdf = target === RenderTarget.pdf;

  // For room-tag memos on the floor plan the memo lives inside the room's local
  // coordinate space, which is already scaled by roomtoFloorDrawingTransform.scale.
  // To make the font appear at the correct absolute size we apply the inverse scale.
  let scale1 = 1;
  /// if (!forPdf) {
  if (true) {
    if (mainPage.isFloorPlan && memo.isRoomTag && room?.roomtoFloorDrawingTransform) {
      scale1 = 1 / room?.roomtoFloorDrawingTransform?.scale || 1;
      fontSize = fontSize * scale1;
    }
  }

  // Split the display text into individual lines for separate rendering.
  const ssLines = s.split("\n");

  const memoLayout = classicMemoLayout(s, fontSize, scale1, memo.isRoomTag, measureText);
  fontSize = memoLayout.fontSize;
  const padAmount = memoLayout.padding;
  let totalWidth = memoLayout.width;
  let lineHeight = memoLayout.lineHeight;
  const totalHeight = memoLayout.height * sizeFromNestedRoomPlan;

  // PDF rendering uses the original (unscaled) fontSize for line height to avoid
  // the floor-plan inverse-scale being applied twice.
  if (forPdf) {
    lineHeight = memoLayout.lineHeight;
  }

  let posX = position.x;
  let posY = position.y;

  // --- View mode (not selected or forPdf) ---
  // Renders the text lines in read-only mode. Clicking selects the memo.
  // For PDF: use flex-column layout to avoid absolute-positioning measurement issues
  // (browser canvas metrics differ from PDFKit metrics, causing unwanted line wraps).
  const pdfTextLines: React.ReactNode[] = [];
  for (let index = 0; index < ssLines.length; index++) {
    pdfTextLines.push(
      <RText key={index} style={{ whiteSpace: "nowrap", fontSize: fontSize + "px",
        lineHeight: lineHeight + "px" }}>{ssLines[index]}</RText>,
    );
  }
  let textElement: React.ReactNode = forPdf ? (
    <RDiv style={{ position: "absolute", top: memoLayout.paddingY + "px", left: padAmount + "px", flexDirection: "column" }}>
      {pdfTextLines}
    </RDiv>
  ) : (
    <MemoViewSvg {...memoLayout} />
  );
  // --- Edit mode (selected and not PDF) ---
  // Replaces the read-only view with an inline editor that lets the user
  // change the memo text and font size.
  // NOTE: for isRoomTag memos this block is intentionally still reached so the
  // font size can be adjusted; the textarea value is derived from the room
  // (room.tag + room.name) and writing to memo.name has no visual effect.
  if (!forPdf && memo.selected && !memo.isRoomTag) {
    textElement = (
      <div
        className="estadaColumn left top"
        style={{
          marginTop: "-50px",
        }}
      >
        <div
          id="fontSizeInput-1"
          className="estadaRow left"
          style={{
            backgroundColor: "var( --primary-color-verylight)",
            borderRadius: "10px",
            padding: "5px",

            margin: "-20px",
            marginBottom: "25px",

            width: `calc(100% + 44px)`,
            minWidth: `max-content`,

            cursor: "default",
          }}
        >
          {noItx("FontSize")}
          <input
            className="input"
            type="number"
            min="4"
            max="40"
            step="2"
            defaultValue={memo.fontSize}
            style={{
              marginLeft: "10px",
              width: "50px",
            }}
            onChange={(ev) => {
              if (fontSizeTimeoutRef.current) {
                clearTimeout(fontSizeTimeoutRef.current);
                fontSizeTimeoutRef.current = null;
              }
              if (memo.isRoomTag) {
              }

              fontSizeTimeoutRef.current = setTimeout(() => {
                memo.fontSize = parseInt(ev.target.value);
                memo.raiseOnChange("fontSize");
              }, 500);
            }}
            onBlur={(ev) => {
              if (fontSizeTimeoutRef.current) {
                clearTimeout(fontSizeTimeoutRef.current);
                fontSizeTimeoutRef.current = null;
              }
              memo.fontSize = parseInt(ev.target.value);
              memo.raiseOnChange("fontSize");
            }}
            onKeyDown={(ev) => {
              ev.stopPropagation();
            }}
            onKeyUp={(ev) => {
              ev.stopPropagation();
            }}
            onKeyPress={(ev) => {
              ev.stopPropagation();
            }}
            onMouseDown={(ev) => {
              ev.stopPropagation();
            }}
            onMouseUp={(ev) => {
              ev.stopPropagation();
            }}
            onClick={(ev) => {
              ev.stopPropagation();
            }}
            onDoubleClick={(ev) => {
              ev.stopPropagation();
            }}
            onContextMenu={(ev) => {
              ev.stopPropagation();
            }}
            onWheel={(ev) => {
              ev.stopPropagation();
            }}
          />
        </div>

        <textarea
          className="textarea"
          style={{
            width: "100px",
            minWidth: `${totalWidth + 20 * sizeFromNestedRoomPlan}px`,
            minHeight: `${totalHeight + 20 * sizeFromNestedRoomPlan}px`,
            margin: `-20px`,
            padding: "10px",
            paddingTop: `${(fontSize - 10) * 0.7}px`,
            whiteSpace: "nowrap",
            fontSize: fontSize + "px",
            lineHeight: lineHeight + "px",
            border: "2px solid orange",
            borderRadius: "10px",
            outline: "none",
          }}
          value={s}
          onChange={(ev) => {
            memo.name = ev.target.value;
            memo.raiseOnChange("name");
          }}
          onBlur={(ev) => {
            memo.name = ev.target.value.trim();
            memo.raiseOnChange("name");
          }}
          onKeyDown={(ev) => {
            ev.stopPropagation();
          }}
          onKeyUp={(ev) => {
            ev.stopPropagation();
          }}
          onKeyPress={(ev) => {
            ev.stopPropagation();
          }}
          onMouseDown={(ev) => {
            ev.stopPropagation();
          }}
          onMouseUp={(ev) => {
            ev.stopPropagation();
          }}
          onClick={(ev) => {
            ev.stopPropagation();
          }}
          onDoubleClick={(ev) => {
            ev.stopPropagation();
          }}
          onContextMenu={(ev) => {
            ev.stopPropagation();
          }}
          onWheel={(ev) => {
            ev.stopPropagation();
          }}
        ></textarea>
      </div>
    );
  }

  let style: any = forPdf
    ? {
        left: `${posX - totalWidth / 2}px`,
        top: `${posY - totalHeight / 2}px`,
        padding: "10px",
        display: "flex",
        flexDirection: "column",

        borderColor: "#423636",
        borderWidth: "1px",
        borderStyle: "solid",
        backgroundColor: "white",
        borderRadius: "10px",
      }
    : {
        left: `${posX}px`,
        top: `${posY}px`,
        marginLeft: `${-totalWidth / 2}px`,
        marginTop: `${-totalHeight / 2}px`,
      };

  return (
    <RDiv
      className={
        "memo " + (memo.selected ? " selected" : "") + " " + (memo.isRoomTag ? "roomTag" : "") + " " + touchSelected
      }
      id={memo.id}
      ref={thisElement}
      style={{
        ...style,
        cursor: memo.isRoomTag && memo.selected ? "pointer" : undefined,
        position: "absolute",
        padding: forPdf ? 0 : `${fontSize * 0.7}px`,
        width: totalWidth + "px",
        height: totalHeight + "px",
        borderRadius: `${memoLayout.radius}px`,
        borderWidth: `${memoLayout.borderWidth}px`,
        borderColor: memoLayout.borderColor,
        ...(!forPdf && !(memo.selected && !memo.isRoomTag) ? {
          padding: 0, borderWidth: 0, backgroundColor: "transparent",
          outline: memo.selected ? "2px solid orange" : undefined,
        } : {}),
      }}
      // Double-click: for room-tag memos open the room's page;
      // for regular memos the native MoveHandler.onDblClick already handles this.
      onDoubleClick={(ev) => {
        ev.stopPropagation();
        if (memo.isRoomTag) {
          const room2 = (memo.parent as Page)?.parentRoom;
          if (room2) {
            room2.treeViewModel.handleOpen();
          }
        }
      }}
      // Single click: room-tag memos select the room (not the memo itself) so the
      // room properties appear in the panel. Regular memos select themselves.
      onClick={(ev) => {
        ev.stopPropagation();
        if (memo.isRoomTag) {
          const room = (memo.parent as Page)?.parentRoom;
          if (room) {
            state.selectionManager.selectedObject = room;
          }
        } else {
          state.selectionManager.selectedObject = memo;
        }
      }}
    >
      {textElement}
    </RDiv>
  );
}
